import { Button, ButtonVariants } from '@components/button/Button';
import cx from 'classnames';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { IconButton } from '@components/iconButton/IconButton';
import { useHtmlId } from '@shared/hooks/useHtmlId';
import { useMemo } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import {
  isBlankDisclaimerRow,
  type EventDisclaimerFormItem,
  type EventFormData,
} from '../RestaurantEventForm';
import styles from './DisclaimerForm.scss';

interface DisclaimerFormProps {
  control: Control<EventFormData>;
  index: number;
  /** Card opened via Edit: Done returns to card view. */
  onDoneCardEdit?: () => void;
  /** New disclaimer: Done returns to card view when title is filled. */
  onDoneNewDisclaimer?: () => void;
  /** When this is the only disclaimer row, resets fields to empty defaults (shown instead of delete). */
  onClearRow?: () => void;
  /** When set with `removeTooltipId`, shows delete control for this row (multiple rows). */
  onRemove?: () => void;
  /** Required when `onRemove` is set; used for the tooltip anchor id. */
  removeTooltipId?: string;
}

export const DisclaimerForm = ({
  control,
  index,
  onDoneCardEdit,
  onDoneNewDisclaimer,
  onClearRow,
  onRemove,
  removeTooltipId,
}: DisclaimerFormProps) => {
  const titleField = `disclaimers.${index}.title` as FieldPath<EventFormData>;
  const descriptionField =
    `disclaimers.${index}.description` as FieldPath<EventFormData>;
  const rowPath = `disclaimers.${index}` as FieldPath<EventFormData>;

  const row = useWatch({ control, name: rowPath }) as
    | EventDisclaimerFormItem
    | undefined;

  const areRequiredFieldsFilled = useMemo(() => {
    return String(row?.title ?? '').trim() !== '';
  }, [row?.title]);

  const doneButtonDisabled = !areRequiredFieldsFilled;
  const clearTooltipId = useHtmlId('disclaimer-form-clear');

  const showRemoveDisclaimerTopRight =
    onClearRow != null || (onRemove != null && removeTooltipId != null);

  const handleDoneClick = () => {
    if (!areRequiredFieldsFilled) return;
    onDoneNewDisclaimer?.();
    onDoneCardEdit?.();
  };

  return (
    <div className={styles.disclaimerFormCard}>
      {showRemoveDisclaimerTopRight ? (
        <div className={styles.removeTopRight}>
          {onClearRow != null ? (
            <IconButton
              key="disclaimer-form-clear"
              ariaLabel="Clear"
              className={cx(
                styles.eventModalActionIconButton,
                styles.iconButtonDefaultStroke,
              )}
              dataTooltipId={clearTooltipId}
              disabled={isBlankDisclaimerRow(row)}
              iconName="rotateCcw"
              onClick={onClearRow}
              tooltipContent="Clear"
            />
          ) : onRemove != null && removeTooltipId != null ? (
            <IconButton
              key={`disclaimer-form-delete-${removeTooltipId}`}
              ariaLabel="Delete"
              className={cx(
                styles.eventModalActionIconButton,
                styles.iconButtonDefaultStroke,
              )}
              dataTooltipId={removeTooltipId}
              iconName="trash"
              onClick={onRemove}
              tooltipContent="Delete"
            />
          ) : null}
        </div>
      ) : null}
      <div className={styles.disclaimerFormCardContent}>
        <div className={styles.formFields}>
          <ControlledFormInput<EventFormData>
            control={control}
            name={titleField}
            type="text"
            label="Disclaimer text"
            rules={{
              validate: (value) => {
                if (isBlankDisclaimerRow(row)) return true;
                return (
                  String(value ?? '').trim() !== '' ||
                  'Disclaimer text is required'
                );
              },
            }}
            variant={LayoutVariant.Vertical}
            labelClassName={styles.fieldLabel}
            darkMode
          />

          <div className={styles.descriptionField}>
            <ControlledFormTextareaAutosize<EventFormData>
              control={control}
              name={descriptionField}
              label="Additional information (optional)"
              minRows={1}
              maxRows={4}
              variant={LayoutVariant.Vertical}
              labelClassName={styles.fieldLabel}
              ariaLabel="Additional information (optional)"
              darkMode
            />
          </div>

          <div className={styles.formRowSecondary}>
            <Button
              type="button"
              label="Done"
              variant={ButtonVariants.Secondary}
              className={styles.doneButton}
              isDisabled={doneButtonDisabled}
              onClick={handleDoneClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
